/* eslint-disable react-refresh/only-export-components */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { fetchRiskMapData, fetchTasks } from '@/api/dashboard';
import { HelpSheet } from '@/components/shared/HelpSheet';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { useActivityFeedStore } from '@/store/activityFeedStore';
import { useSessionStore } from '@/store/sessionStore';
import KpiCards from '../-components/KpiCards';
import MapCard from '../-components/MapCard';
import TasksList from '../-components/TasksList';
import { useDashboardTasks } from '../-hooks/useDashboardTasks';
import { useDismissTask } from '../-hooks/useDismissTask';

export const Route = createFileRoute('/(client)/dashboard/(pages)/')({
  loader: () =>
    Promise.all([
      queryClient.ensureQueryData({
        queryKey: ['dashboard', 'risk-map'],
        queryFn: fetchRiskMapData,
      }),
      queryClient.ensureQueryData({
        queryKey: ['dashboard', 'tasks'],
        queryFn: fetchTasks,
      }),
    ]),
  component: DashboardPage,
});

function DashboardPage() {
  const user = useSessionStore(s => s.user);
  const [mapData] = Route.useLoaderData();
  const { getTranslation: t } = useTranslator({ page: 'dashboard' });
  const { changeStatus } = useDismissTask();
  const setFeedOpen = useActivityFeedStore(s => s.setOpen);
  const [helpOpen, setHelpOpen] = useState(false);

  const { data: tasksData } = useDashboardTasks();

  return (
    <div className='@container flex-1 min-h-0 overflow-y-auto flex flex-col gap-4 p-6 pb-8'>
      <div className='animate-in fade-in-0 slide-in-from-top-2 duration-400'>
        <h1 className='text-2xl font-bold tracking-tight text-foreground'>
          {t('welcome', { name: user?.firstName ?? '' })}{' '}
          <span role='img' aria-label='wave'>
            👋
          </span>
        </h1>
        <p className='text-sm text-muted-foreground mt-1'>{t('subtitle')}</p>
      </div>

      <div className='flex items-center justify-between'>
        <h2 className='text-xl font-bold tracking-tight text-foreground'>
          {t('overview')}
        </h2>
        <button
          type='button'
          onClick={() => setHelpOpen(true)}
          className='flex items-center gap-1.5 text-sm text-primary hover:underline'
        >
          <i className='fa-regular fa-circle-question text-base' />
          {t('help')}
        </button>
      </div>

      <KpiCards
        countries={mapData ?? []}
        tasksData={tasksData}
        onOpenTasks={() => setFeedOpen(true)}
      />

      <div className='grid grid-cols-1 @5xl:grid-cols-3 gap-4 flex-1 min-h-[420px]'>
        <MapCard countries={mapData ?? []} />
        <div
          className='flex flex-col h-full min-h-0 animate-in fade-in-0 duration-500'
          style={{ animationDelay: '160ms', animationFillMode: 'both' }}
        >
          <TasksList
            tasks={tasksData?.data ?? []}
            onStatusChange={changeStatus}
            onViewAll={() => setFeedOpen(true)}
          />
        </div>
      </div>

      <HelpSheet
        articleId='8508473-3-important-icons-to-navigate-the-platform'
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
}
